import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Student } from '../../data/types';
import { dataService } from '../../services/dataService';
import GameNavHeaderButtons from '../common/GameNavHeaderButtons';
import { GameIcons } from '../styles/GameIcons';
import { motion } from 'framer-motion';

const StudentLayoutV4: React.FC = () => {
  const [student, setStudent] = useState<Student | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState('Ready to learn something new today?');
  const navigate = useNavigate();
  
  useEffect(() => {
    // Fetch student data
    const fetchData = async () => {
      try {
        // Get the first student from the list for demo purposes
        const students = await dataService.getStudents();
        if (students.length > 0) {
          setStudent(students[0]);
        }
        
        // Set personalized welcome message based on time of day
        const hour = new Date().getHours();
        if (hour < 12) {
          setWelcomeMessage('Good morning! Ready for some math challenges?');
        } else if (hour < 17) {
          setWelcomeMessage('Good afternoon! Time to boost your math skills!');
        } else {
          setWelcomeMessage('Good evening! Let\'s finish the day with some math!');
        }
      } catch (error) {
        console.error('Error fetching student data:', error);
      }
    };
    
    fetchData();
  }, []);
  
  const handleLogout = () => {
    // Clear all student-related data from localStorage
    localStorage.removeItem('activeStudentId');
    localStorage.removeItem('isStudentSession');
    localStorage.removeItem('activeRole');
    
    // Force navigation to the main screen
    window.location.href = '/';
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-400 via-purple-300 to-blue-400 text-gray-800 overflow-hidden relative">
      {/* Decorative elements - fewer and more subtle floating shapes */}
      <div className="absolute top-10 left-10 w-16 h-16 bg-yellow-300 rounded-full opacity-20 animate-bounce"></div>
      <div className="absolute bottom-20 right-1/4 w-14 h-14 bg-blue-300 rounded-full opacity-20 animate-pulse"></div>
      
      {/* Reduced number of stars in the background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-5 left-1/3 w-2 h-2 bg-white rounded-full opacity-50 animate-pulse"></div>
        <div className="absolute bottom-20 right-1/3 w-2 h-2 bg-white rounded-full opacity-50 animate-pulse"></div>
      </div>
      
      {/* Header with logo, navigation buttons, welcome message, and profile */}
      <header className="py-2 px-4 bg-white bg-opacity-40 backdrop-filter backdrop-blur-sm border-b border-purple-200">
        <div className="flex items-center justify-between">
          {/* Logo on the left with animated effects */}
          <motion.div 
            className="flex items-center relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Subtle shimmer effect */}
            <motion.div 
              className="absolute -inset-2 pointer-events-none z-0 opacity-50"
              animate={{ 
                background: [
                  'radial-gradient(circle, rgba(255,255,255,0) 60%, rgba(173,216,230,0.3) 100%)',
                  'radial-gradient(circle, rgba(255,255,255,0) 50%, rgba(173,216,230,0.5) 100%)',
                  'radial-gradient(circle, rgba(255,255,255,0) 60%, rgba(173,216,230,0.3) 100%)'
                ]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
            
            {/* Animated stars around logo */}
            <motion.div 
              className="absolute -inset-4 pointer-events-none z-0"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              {[...Array(5)].map((_, i) => (
                <motion.div 
                  key={i}
                  className="absolute w-2 h-2 bg-yellow-300 rounded-full"
                  style={{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                  }}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.7, 1, 0.7],
                  }}
                  transition={{
                    duration: 2 + Math.random() * 2,
                    repeat: Infinity,
                    delay: Math.random() * 2,
                  }}
                />
              ))}
            </motion.div>
            
            {/* Logo with hover effect - no background */}
            <motion.div 
              className="relative z-10"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <img 
                src="/assets/images/swunmath_logo.png" 
                alt="SwunMath Logo" 
                className="h-12 relative z-10" 
              />
            </motion.div>
          </motion.div>
          {/* Navigation Buttons (compact) */}
          <div className="flex-1 flex justify-center">
            <GameNavHeaderButtons />
          </div>
          {/* Only logout button on the right */}
          <div className="flex items-center space-x-3">
            <button 
              onClick={handleLogout}
              className="px-4 py-2 flex items-center gap-2 rounded-lg text-base font-bold bg-gradient-to-br from-red-400 to-red-600 bg-opacity-80 shadow-sm border border-white/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/40 hover:scale-105 hover:-translate-y-0.5"
              title="Logout"
              style={{ fontFamily: 'Fredoka, sans-serif', letterSpacing: '0.03em', color: '#fff' }}
            >
              <span className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="game-icon drop-shadow">
                  <path d="M17,7l-1.41,1.41L18.17,11H8v2h10.17l-2.58,2.58L17,17l5-5L17,7z" fill="#fff" />
                  <path d="M5,5h7V3H5C3.9,3,3,3.9,3,5v14c0,1.1,0.9,2,2,2h7v-2H5V5z" fill="#fff" />
                </svg>
              </span>
              <span className="text-white font-bold drop-shadow-sm" style={{fontSize: '1rem'}}>Logout</span>
            </button>
          </div>
        </div>
      </header>
      
      <main className="p-4">
        <Outlet />
      </main>
      
      {/* Footer with game-style design - lighter background */}
      <footer className="p-4 text-center bg-white bg-opacity-40 backdrop-filter backdrop-blur-sm border-t border-purple-200 mt-auto">
        <p className="text-sm text-indigo-800">&copy; {new Date().getFullYear()} SwunMath Universe. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default StudentLayoutV4;
